import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ContactForm } from '@/components/contact/contact-form'

const nextSteps = [
    {
        title: "Tell me about your space",
        text: "Share the rooms you're thinking about, what you already love and any timing you have in mind."
    },
    {
        title: "I'll reply by email",
        text: "If it feels like a good fit, we'll set up a relaxed first conversation about your home."
    },
    {
        title: "We shape a plan together",
        text: "From there, we agree on the scope and the best way to begin, room by room."
    }
]

const ContactPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Contact"
                description="Planning a single room or a whole home? Tell me about your space and what you would like to change, and I will reply by email."
            />

            <section id='contact' className='mx-auto grid max-w-7xl gap-14 px-5 pt-12 pb-24 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:pt-20 lg:pb-32'>
                <div className='lg:col-span-5'>
                    <Eyebrow>Contact</Eyebrow>

                    <h1 className='mt-7 font-heading text-5xl leading-[1.04] tracking-tight sm:text-6xl'>
                        Let's talk about your home
                    </h1>

                    <p className='mt-6 text-lg leading-relaxed text-muted-foreground'>
                        Whether you're planning a single room or a whole house, tell me a little about your space and what you'd like to change. I read every message personally.
                    </p>

                    <ol className='mt-12 space-y-8'>
                        {nextSteps.map((step, index) => (
                            <li key={step.title} className='flex gap-5'>
                                <span className='inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-primary/40 font-heading text-sm text-primary tabular-nums'>
                                    {index + 1}
                                </span>

                                <div>
                                    <h2 className='font-heading text-xl'>
                                        {step.title}
                                    </h2>

                                    <p className='mt-1.5 leading-relaxed text-muted-foreground'>
                                        {step.text}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>

                <div className='lg:col-span-7'>
                    <ContactForm />
                </div>
            </section>
        </Layout>
    )
}

export default ContactPage
